import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useI18n } from "../../../hooks/i18n-hooks";
import { StyleSheet, View } from "react-native";
import VStack from "../../../ui_atoms/VStack";
import {
    calculatePayrollBreakdownMaxWorkDays,
    FormData,
    PayrollBreakdownData,
    PayrollBreakdownEditorProps,
} from "./PayrollBreakdownEditor";
import Gap from "../../../ui_atoms/Gap";
import { Controller, useForm } from "react-hook-form";
import { BodyMediumText, BodySmallText, LinkSmallText } from "../../FormattedText";
import { EarningType } from "../../../__generated__/graphql";
import PayrollBreakdownCommon from "./PayrollBreakdownCommon";
import PayrollBreakdownHourlyInfo from "./PayrollBreakdownHourlyInfo";
import PayrollBreakdownTotalInfo from "./PayrollBreakdownTotalInfo";
import PayrollBreakdownAbsences from "./PayrollBreakdownAbsenses";
import DropdownField from "../../DropdownField";
import { useTheme } from "../../../hooks/use-theme";
import { SHORT_DATE, useDateFormatter } from "../../../hooks/date";
import { useDeepCompareEffect } from "../../../hooks/use-deep-compare-effect";
import { useChainInputFocus } from "../../../hooks/use-chain-input-focus";
import { useIntercom } from "../../../hooks/intercom";
import QuantityField from "../../QuantityField";
import { buildPayrollBreakdownFormFromModel } from "./utils";
import PayrollBreakdownExtras from "./PayrollBreakdownExtras";
import { isVacationFreeWorkingTime, toFixedIfNecessary } from "../../../utils";

const PayrollBreakdownEditorHourly: React.FC<PayrollBreakdownEditorProps> = ({
    payrollBreakdown,
    mission,
    vacationStatus,
    onValidData,
    onChange,
    needWorkDays,
    isFromTimesheet,
    timeSheetsNeedCompletion,
    isEmptyPeriod,
    setCustomSummaryRows,
}) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const styles = useStyles();
    const formatDate = useDateFormatter();
    const { presentArticleId } = useIntercom();
    const hoursPerDay = useMemo(() => {
        return parseFloat(vacationStatus?.contractHoursPerWeekFullTime || "0") / 5;
    }, [vacationStatus]);
    const {
        control,
        watch,
        formState: { isValid },
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: buildPayrollBreakdownFormFromModel(payrollBreakdown, hoursPerDay),
    });

    const totalWork100Hours = watch("totalWork100Hours");
    const totalWork115Hours = watch("totalWork115Hours");
    const totalWork125Hours = watch("totalWork125Hours");
    const totalWork150Hours = watch("totalWork150Hours");
    const totalWork200Hours = watch("totalWork200Hours");
    const absences = watch("absences");
    const effectiveFees = watch("effectiveFees");
    const dynamicFees = watch("dynamicFees");
    const bonuses = watch("bonuses");
    const auxiliarySalaries = watch("auxiliarySalaries");

    const totalWorkedDays = watch("totalWorkedDays");
    // mission that has less than 2500 minutes (~5 days) of vacation is a mission that is bellow 20%.
    // in this case we display vacations in hours by default
    const [vacationInputType, setVacationInputType] = useState(
        vacationStatus.contractVacationMinutes < 2500 ? "hours" : "days",
    );
    const [usedVacationDays, setUsedVacationDays] = useState(
        payrollBreakdown.usedVacationMinutes != null
            ? toFixedIfNecessary(payrollBreakdown.usedVacationMinutes / 60 / hoursPerDay, 2)
            : undefined,
    );
    const [usedVacationInputValue, setUsedVacationInputValue] = useState<number | undefined>(
        usedVacationDays || 0,
    );
    const availableVacationAmountMinutes = useMemo(() => {
        if (payrollBreakdown.missionInfo.earningType === EarningType.Daily) {
            const hoursPerDay = payrollBreakdown.missionInfo.workingTimeClassicSpec?.dailyHours;
            if (!hoursPerDay) {
                throw new Error("Daily hours not defined for daily earning type");
            }
            const availableAmount =
                (((totalWork100Hours || 0) +
                    (totalWork115Hours || 0) +
                    (totalWork125Hours || 0) +
                    (totalWork150Hours || 0) +
                    (totalWork200Hours || 0)) /
                    hoursPerDay) *
                    vacationStatus.contractVacationIndemnity +
                vacationStatus.availableVacationAmount;

            return (availableAmount / vacationStatus.contractBaseSalary) * hoursPerDay * 60;
        } else {
            const availableAmount =
                (totalWork100Hours || 0) * vacationStatus.contractVacationIndemnity +
                (totalWork115Hours || 0) * vacationStatus.contractVacationIndemnity +
                (totalWork125Hours || 0) * vacationStatus.contractVacationIndemnity +
                (totalWork150Hours || 0) * vacationStatus.contractVacationIndemnity +
                (totalWork200Hours || 0) * vacationStatus.contractVacationIndemnity +
                vacationStatus.availableVacationAmount;

            return (availableAmount / vacationStatus.contractBaseSalary) * 60;
        }
    }, [
        totalWork100Hours,
        totalWork115Hours,
        totalWork125Hours,
        totalWork150Hours,
        totalWork200Hours,
        vacationStatus.availableVacationAmount,
        vacationStatus.contractBaseSalary,
        vacationStatus.contractVacationIndemnity,
        payrollBreakdown.missionInfo.workingTimeClassicSpec,
        payrollBreakdown.missionInfo.earningType,
    ]);

    const endOfMonthVacationTotal = useCallback(
        (inputType: string) => {
            const endOfMonthMinutes =
                availableVacationAmountMinutes - (usedVacationDays || 0) * 60 * hoursPerDay;
            if (inputType === "days") {
                return toFixedIfNecessary(endOfMonthMinutes / hoursPerDay / 60, 2);
            } else if (inputType === "hours") {
                return toFixedIfNecessary(endOfMonthMinutes / 60, 2);
            } else {
                return toFixedIfNecessary(endOfMonthMinutes, 0);
            }
        },
        [availableVacationAmountMinutes, usedVacationDays, hoursPerDay],
    );

    const availableVacationAmount = useCallback(
        (inputType: string) => {
            if (inputType === "days") {
                return toFixedIfNecessary(availableVacationAmountMinutes / hoursPerDay / 60, 2);
            } else if (inputType === "hours") {
                return toFixedIfNecessary(availableVacationAmountMinutes / 60, 2);
            } else {
                return toFixedIfNecessary(availableVacationAmountMinutes, 0);
            }
        },
        [availableVacationAmountMinutes, hoursPerDay],
    );
    useDeepCompareEffect(() => {
        const data: PayrollBreakdownData = isFromTimesheet
            ? {
                  usedVacationMinutes: undefined,
                  usedVacationAmount: undefined,
                  totalWork100: undefined,
                  totalWork125: undefined,
                  totalWork150: undefined,
                  totalWork200: undefined,
                  totalWorkedDays: undefined,
                  absences: undefined,
                  dynamicFees: dynamicFees.map((f) => ({
                      refId: f.fee.id,
                      count: f.count || 0,
                  })),
                  bonuses: bonuses,
                  auxiliarySalaries: auxiliarySalaries,
                  effectiveFees: effectiveFees,
              }
            : {
                  usedVacationMinutes: usedVacationDays
                      ? Math.min(
                            // We usually round to the nearest minute,
                            Math.round(usedVacationDays * 60 * hoursPerDay),
                            // but if the result is higher than the available vacations amount minutes, we replace it with the availableVacationAmountMinutes
                            toFixedIfNecessary(availableVacationAmountMinutes, 0),
                        )
                      : 0,
                  totalWork100: totalWork100Hours != null ? totalWork100Hours * 60 : undefined,
                  totalWork115: (totalWork115Hours || 0) * 60,
                  totalWork125: (totalWork125Hours || 0) * 60,
                  totalWork150: (totalWork150Hours || 0) * 60,
                  totalWork200: (totalWork200Hours || 0) * 60,
                  totalWorkedDays: needWorkDays ? totalWorkedDays : undefined,
                  absences: absences,
                  dynamicFees: dynamicFees.map((f) => ({
                      refId: f.fee.id,
                      count: f.count || 0,
                  })),
                  bonuses: bonuses,
                  auxiliarySalaries: auxiliarySalaries,
                  effectiveFees: effectiveFees,
              };
        onChange(data);

        if (isValid) {
            if (isFromTimesheet) {
                onValidData(data);
            } else if (usedVacationDays != null && !hasOverflow) {
                // usedVacationMinutes is not managed
                // by the form so we need to check also that it has been set.
                onValidData(data);
            } else {
                // If usedVacationMinutes is not set, form is not valid
                onValidData(null);
            }
        } else {
            onValidData(null);
        }
    }, [
        isValid,
        isFromTimesheet,
        totalWorkedDays,
        onValidData,
        needWorkDays,
        totalWork100Hours,
        totalWork115Hours,
        totalWork125Hours,
        totalWork150Hours,
        totalWork200Hours,
        usedVacationDays,
        vacationStatus.contractBaseSalary,
        bonuses,
        auxiliarySalaries,
        effectiveFees,
        absences,
        dynamicFees,
    ]);

    type TotalWorkFields = Pick<
        FormData,
        | "totalWork100Hours"
        | "totalWork115Hours"
        | "totalWork125Hours"
        | "totalWork150Hours"
        | "totalWork200Hours"
    >;
    const totalWorkFields = [
        "totalWork100Hours",
        "totalWork115Hours",
        "totalWork125Hours",
        "totalWork150Hours",
        "totalWork200Hours",
    ] as Array<keyof TotalWorkFields>;

    const totalHours = useMemo(() => {
        return [
            totalWork100Hours,
            totalWork115Hours,
            totalWork125Hours,
            totalWork150Hours,
            totalWork200Hours,
        ].reduce(
            (acc, value) => {
                if (value != null) {
                    if (acc == null) {
                        return value;
                    }
                    return acc + value;
                }
                return acc;
            },
            undefined as number | undefined,
        );
    }, [
        totalWork100Hours,
        totalWork115Hours,
        totalWork125Hours,
        totalWork150Hours,
        totalWork200Hours,
    ]);

    const handleVacationTypeChange = useCallback(
        (newType: string) => {
            const currentMinutes = (() => {
                if (usedVacationInputValue == null || isNaN(usedVacationInputValue)) return 0;
                if (vacationInputType === "days") return usedVacationInputValue * hoursPerDay * 60;
                if (vacationInputType === "hours") return usedVacationInputValue * 60;
                return usedVacationInputValue;
            })();
            if (newType === "days") {
                setUsedVacationInputValue(toFixedIfNecessary(currentMinutes / hoursPerDay / 60, 2));
            } else if (newType === "hours") {
                setUsedVacationInputValue(toFixedIfNecessary(currentMinutes / 60, 2));
            } else {
                setUsedVacationInputValue(toFixedIfNecessary(currentMinutes, 0));
            }
            setVacationInputType(newType);
        },
        [usedVacationInputValue, vacationInputType, hoursPerDay],
    );

    const { chainInputFocus } = useChainInputFocus();

    const showVacationInfo = useMemo(() => {
        const spec = mission?.currentContract?.workingTimeClassicSpec;
        return !!spec?.repetition && !isVacationFreeWorkingTime(spec);
    }, [mission]);

    useEffect(() => {
        // if filled by timesheet we do not need to compute the minutes from the input value
        if (!isFromTimesheet) {
            if (usedVacationInputValue == null || isNaN(usedVacationInputValue)) {
                setUsedVacationDays(undefined);
            } else if (vacationInputType === "days") {
                setUsedVacationDays(usedVacationInputValue);
            } else if (vacationInputType === "hours") {
                setUsedVacationDays(usedVacationInputValue / hoursPerDay);
            } else {
                setUsedVacationDays(usedVacationInputValue / 60 / hoursPerDay);
            }
        }
    }, [
        usedVacationInputValue,
        hoursPerDay,
        setUsedVacationDays,
        vacationInputType,
        isFromTimesheet,
    ]);

    const payrollBreakdownExtras = useMemo(
        () =>
            ((isFromTimesheet && !timeSheetsNeedCompletion) || !isFromTimesheet) && (
                <PayrollBreakdownExtras
                    control={control}
                    clientDeductions={mission?.currentContract?.clientDeductions || []}
                    payrollBreakdown={payrollBreakdown}
                    setCustomSummaryRows={setCustomSummaryRows}
                    canManageAbsences={!isEmptyPeriod && !isFromTimesheet}
                />
            ),
        [
            timeSheetsNeedCompletion,
            control,
            mission?.currentContract?.clientDeductions,
            payrollBreakdown,
            setCustomSummaryRows,
            isEmptyPeriod,
            isFromTimesheet,
        ],
    );

    const hasOverflow = useMemo(() => {
        return endOfMonthVacationTotal(vacationInputType) < 0;
    }, [endOfMonthVacationTotal, vacationInputType]);

    const maxWorkDays = useMemo(() => {
        if (!mission?.startDate || !payrollBreakdown) {
            return 0;
        }
        return calculatePayrollBreakdownMaxWorkDays({
            contractStart: mission.startDate,
            contractEnd: mission.endDate,
            payrollBreakdownStart: payrollBreakdown.startDate,
            payrollBreakdownEnd: payrollBreakdown.endDate,
        });
    }, [mission?.endDate, mission?.startDate, payrollBreakdown]);

    return (
        <VStack v={1}>
            {isFromTimesheet ? (
                <>
                    <VStack h="2/3">
                        <PayrollBreakdownHourlyInfo
                            payrollBreakdown={payrollBreakdown}
                            disabled={timeSheetsNeedCompletion}
                        />
                        <Gap gap={0.25} />
                        <PayrollBreakdownTotalInfo
                            payrollBreakdown={payrollBreakdown}
                            vacationStatus={vacationStatus}
                            disabled={timeSheetsNeedCompletion}
                        />
                        <Gap gap={1} />
                        <PayrollBreakdownCommon
                            needWorkDays={false}
                            control={control}
                            chainInputFocus={chainInputFocus}
                            clientDeductions={mission?.currentContract?.clientDeductions || []}
                            dynamicFees={dynamicFees}
                            payrollBreakdownType="hourly"
                        />
                        <Gap gap={1} />
                        {payrollBreakdownExtras}
                    </VStack>
                    <PayrollBreakdownAbsences absences={payrollBreakdown.absences} />
                </>
            ) : (
                <VStack h="2/3">
                    <BodyMediumText>
                        {i18n.t("payroll_breakdown.hourly.overtime_paid.title")}
                    </BodyMediumText>
                    <Gap gap={1} />
                    {totalWorkFields.map((k, idx) => (
                        <Controller
                            key={k}
                            name={k}
                            control={control}
                            rules={{
                                validate: (value) => {
                                    if (value === undefined || value === null) {
                                        return i18n.t("payroll_breakdown.error_not_set");
                                    }
                                    return true;
                                },
                            }}
                            render={({
                                field: { onChange, value, name },
                                fieldState: { error },
                            }) => (
                                <QuantityField
                                    {...chainInputFocus(name)}
                                    isSelfContained
                                    label={i18n.t(`payroll_breakdown.field_${k}`)}
                                    value={value}
                                    onChange={onChange}
                                    decimalPlaces={2}
                                    error={error?.message}
                                    showErrorText
                                    style={[
                                        styles.stackedField,
                                        idx !== 0 ? styles.stackedFieldNoTop : null,
                                        idx !== totalWorkFields.length - 1
                                            ? styles.stackedFieldNoBottom
                                            : null,
                                        { alignSelf: "flex-start" },
                                    ]}
                                />
                            )}
                        />
                    ))}
                    <Gap gap="1/3" />
                    <BodySmallText color={colors.greyAccessible}>
                        {i18n.t("payroll_breakdown.worked_hours.description")}
                    </BodySmallText>
                    <LinkSmallText
                        onPress={() => {
                            void presentArticleId("10485414");
                        }}
                    >
                        {i18n.t("payroll_breakdown.worked_hours.link")}
                    </LinkSmallText>
                    <Gap gap={1} />
                    <PayrollBreakdownCommon
                        needWorkDays={needWorkDays}
                        control={control}
                        chainInputFocus={chainInputFocus}
                        maxWorkDays={maxWorkDays}
                        minWorkDays={totalHours != null ? Math.ceil(totalHours / 24) : undefined}
                        clientDeductions={mission?.currentContract?.clientDeductions || []}
                        dynamicFees={dynamicFees}
                        payrollBreakdownType="hourly"
                    />
                    {showVacationInfo && (
                        <>
                            <Gap gap={1} />
                            <BodyMediumText>
                                {i18n.t("payroll_breakdown.hourly.vacations.title")}
                            </BodyMediumText>
                            <Gap gap={0.5} />
                            <View
                                style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                }}
                            >
                                <QuantityField
                                    {...chainInputFocus("usedVacations")}
                                    value={usedVacationInputValue}
                                    onChange={setUsedVacationInputValue}
                                    decimalPlaces={
                                        ["days", "hours"].includes(vacationInputType) ? 2 : 0
                                    }
                                    showErrorText={false}
                                    style={{
                                        alignSelf: "flex-start",
                                        flex: 1,
                                        minWidth: "50%",
                                    }}
                                    error={hasOverflow}
                                />
                                <Gap gap={1 / 2} />
                                <DropdownField<string>
                                    numberOfLines={1}
                                    style={{ flexShrink: 1, flexGrow: 1, flexBasis: "75%" }}
                                    items={[
                                        {
                                            label: i18n.t("payroll_breakdown.vacation_by_days"),
                                            value: "days",
                                        },
                                        {
                                            label: i18n.t("payroll_breakdown.vacation_by_hours"),
                                            value: "hours",
                                        },
                                        {
                                            label: i18n.t("payroll_breakdown.vacation_by_minutes"),
                                            value: "minutes",
                                        },
                                    ]}
                                    onSelect={handleVacationTypeChange}
                                    value={vacationInputType}
                                />
                            </View>
                            <Gap gap={1 / 3} />
                            {hasOverflow ? (
                                <BodySmallText color={colors.error}>
                                    {i18n.t(
                                        vacationInputType === "days"
                                            ? "payroll_breakdown.used_vacation_days_overflow"
                                            : vacationInputType === "hours"
                                              ? "payroll_breakdown.used_vacation_hours_overflow"
                                              : "payroll_breakdown.used_vacation_minutes_overflow",
                                        {
                                            count: availableVacationAmount(vacationInputType),
                                        },
                                    )}
                                </BodySmallText>
                            ) : (
                                <BodySmallText color={colors.greyAccessible}>
                                    {i18n.t(
                                        vacationInputType === "days"
                                            ? "payroll_breakdown.used_vacation_days_info"
                                            : vacationInputType === "hours"
                                              ? "payroll_breakdown.used_vacation_hours_info"
                                              : "payroll_breakdown.used_vacation_minutes_info",
                                        {
                                            count: endOfMonthVacationTotal(vacationInputType),
                                            date: formatDate(
                                                new Date(payrollBreakdown.endDate),
                                                SHORT_DATE,
                                            ),
                                        },
                                    )}
                                </BodySmallText>
                            )}
                        </>
                    )}
                    <Gap gap={1} />
                    {payrollBreakdownExtras}
                </VStack>
            )}
        </VStack>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                stackedField: {
                    marginTop: -2,
                },
                stackedFieldNoTop: {
                    borderTopRightRadius: 0,
                    borderTopLeftRadius: 0,
                },
                stackedFieldNoBottom: {
                    borderBottomRightRadius: 0,
                    borderBottomLeftRadius: 0,
                },
            }),
        [],
    );
};

export default PayrollBreakdownEditorHourly;
